/**
 * The 64px ink rail — workspace navigation, per `design/project/screens/chrome.jsx`.
 *
 * **UX cleanup, 2026-08-25: the rail draws only what works.** It used to carry four disabled
 * items — Inbox, Recent, Search, Notifications — on the argument that a rail with one live
 * icon reads as a half-finished build. That argument expired when People and Settings
 * shipped: there are three live destinations now, and four dead ones beside them read as a
 * broken product rather than an unfinished one. The block is commented out rather than
 * deleted, immediately below, and the notifications half survives in the top bar's bell —
 * which is also where the inbox lands when somebody builds it.
 *
 * The workspace mark is an outlined square holding the company's initial. The *switcher* is
 * on the avatar at the foot of the rail (`KLQ-20260819-007` §6) and appears only for somebody
 * who works for more than one studio — chat1.md §4 draws none, and for the ~100% of people
 * with one company there is still none to draw.
 *
 * **What routes.** My tasks (`/tasks`), Projects (`/projects`), Inbox, People (`/people`, B2),
 * Firms and — for an admin or a manager — Studio settings (`/settings`, B3). Active state is read
 * from the router rather than hard-coded, because with several destinations a rail that always
 * highlights the first one is a rail that lies about where you are.
 *
 * **The settings entry is hidden rather than shown-and-refused.** Every endpoint behind it
 * sits at `MANAGER`, and `GET /workspace/domains` returns the verification token, so there is
 * no read-only version of that screen to fall through to (`KLQ-20260819-008` §2). A control
 * whose only outcome is `403` is not a control. Whether the caller holds that authority is
 * *not* decided here — the rail is a layout component and this is an authorisation-shaped
 * question, so it arrives as a prop from `features/projects/AppChrome`, beside the §E rule
 * about chrome existing at all.
 *
 * This component reads the catalog directly rather than taking label props, unlike the Tier-1
 * primitives beside it (`Button`, `Input`, `LanguageSwitcher`). Those are context-free and
 * usable in any screen family, so their copy belongs to the caller. The chrome is the
 * opposite: one instance, one screen family, seven labels — threading them through props
 * would buy nothing and put the rail's copy somewhere no one would look for it.
 */

import { Link, useRouterState } from '@tanstack/react-router';
import type { LucideProps } from 'lucide-react';
// UX cleanup 2026-08-25: `Bell`, `Clock`, `Inbox` and `Search` belonged to the four dead rail
// items below. Restore them here when those items come back.
// import { Bell, Clock, Folder, Inbox, Search, Settings, Users } from 'lucide-react';
import { Building2, Folder, Inbox, ListChecks, Settings, Users } from 'lucide-react';
import type { ComponentType } from 'react';

import type { MessageKeys } from '@/lib/i18n';
import { useMessages } from '@/lib/i18n';

import styles from './Chrome.module.css';
import type { SwitchableWorkspace } from './AvatarMenu';
import { AvatarMenu } from './AvatarMenu';

/** Lucide at 1.5px, one drawing hand across the product (MCK-20260521-003). */
const ICON_SIZE = 20;
const ICON_STROKE = 1.5;

// interface PlaceholderItem {
//   readonly key: string;
//   readonly labelKey: MessageKeys['app'];
//   readonly Icon: ComponentType<LucideProps>;
// }

interface RoutedItem {
  readonly key: string;
  readonly to: '/tasks' | '/projects' | '/inbox' | '/people' | '/firms' | '/settings';
  readonly labelKey: MessageKeys['app'];
  readonly Icon: ComponentType<LucideProps>;
}

/**
 * The items that go somewhere.
 *
 * **My tasks first, Projects second** (owner, 2026-09-17). The task page is the product's front
 * door: `/` sends everybody who reaches a project to `/tasks` (`HomeRoute`, the server's
 * `landing_for`), and the projects list moved from `/` to `/projects`. This deliberately departs
 * from chat1.md §1, where the projects list was the workspace home — the owner's call, recorded
 * in `MCK-20260521-009` and `KLQ-20260819-003`. My tasks used to be reachable only from the
 * avatar menu; a front door that hides behind a menu is not one.
 */
const ROUTED: readonly RoutedItem[] = [
  { key: 'tasks', to: '/tasks', labelKey: 'chrome.nav.tasks', Icon: ListChecks },
  { key: 'projects', to: '/projects', labelKey: 'chrome.nav.projects', Icon: Folder },
  // B3 (`MCK-20260521-009`). Directly under Projects: since D3 this is the only place a person
  // learns they were pulled into something — no notification mail goes out at all — so it is a
  // destination people come back to, not a setting.
  { key: 'inbox', to: '/inbox', labelKey: 'chrome.nav.inbox', Icon: Inbox },
  { key: 'people', to: '/people', labelKey: 'chrome.nav.people', Icon: Users },
  // The studio's address book (`KLQ-20260820-007`, owner 2026-09-16). Beside People because it
  // answers the same kind of question — *who* — one level out: People is the firm's own staff,
  // Firms is everybody else it works with. Above the spacer, because it is a destination rather
  // than a setting; reading it is membership, and only its write controls need authority.
  { key: 'firms', to: '/firms', labelKey: 'chrome.nav.firms', Icon: Building2 },
];

/** Below the spacer, where a settings entry lives in every product that has one. */
const SETTINGS: RoutedItem = {
  key: 'settings',
  to: '/settings',
  labelKey: 'chrome.nav.settings',
  Icon: Settings,
};

/**
 * UX cleanup, 2026-08-25 — COMMENTED OUT, NOT DELETED.
 *
 * Four disabled icons, two of which (Search, Notifications) were *also* drawn in the top bar:
 * six dead controls in the chrome of every screen in the product. The comment above these
 * items argued that a rail carrying one live icon reads as a half-finished build — that was
 * true when it was written and stopped being true the day People and Settings shipped. The
 * rail now has three live destinations and needs no filler.
 *
 * The notifications half is not cancelled, only moved: it lives in the top bar's bell, which
 * is where the inbox lands when it is built. Uncomment this block to put the rail items back.
 */
// const PLACEHOLDERS: readonly PlaceholderItem[] = [
//   { key: 'inbox', labelKey: 'chrome.nav.inbox', Icon: Inbox },
//   { key: 'recent', labelKey: 'chrome.nav.recent', Icon: Clock },
//   { key: 'search', labelKey: 'chrome.nav.search', Icon: Search },
//   { key: 'notifications', labelKey: 'chrome.nav.notifications', Icon: Bell },
// ];

export interface LeftRailProps {
  /**
   * Whether the rail is slid out **on a phone**. Above 720px the rail is a permanent column
   * and this flag is not read — the rule that hides it lives inside the phone's media query,
   * so there is no second source of truth about the width.
   */
  readonly open: boolean;
  /** Closes it. Called by the backdrop, by Escape, and by following any item in it. */
  readonly onClose: () => void;
  /** Drawn in the workspace mark. Empty while `GET /workspace` is still in flight. */
  readonly workspaceInitial: string;
  /**
   * Whether this caller may reach the studio's settings — admin or manager.
   *
   * Decided by `AppChrome` from the caller's own row in the member list, and `false` while
   * that list is in flight, so the item appears once rather than appearing and then vanishing
   * for somebody who may not use it.
   */
  readonly canAdministerStudio: boolean;
  /** The signed-in person, for the avatar's initials and its accessible name. */
  readonly userLabel: string;
  readonly userInitials: string;
  /** Every company the caller works for. Two or more add a company section to the avatar. */
  readonly workspaces: readonly SwitchableWorkspace[];
  readonly activeWorkspaceId: string | null;
  /** Ends the session. Threaded through rather than called here — see `AvatarMenu`. */
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
}

export function LeftRail({
  open,
  onClose,
  workspaceInitial,
  canAdministerStudio,
  userLabel,
  userInitials,
  workspaces,
  activeWorkspaceId,
  onSignOut,
  signingOut,
}: LeftRailProps) {
  const t = useMessages('app');
  // UX cleanup 2026-08-25: only the commented-out placeholders needed this word.
  // const unavailable = t('chrome.unavailable');
  const path = useRouterState({ select: (state) => state.location.pathname });

  return (
    <>
      {/* The backdrop exists only while the drawer is open and only on a phone. A press
          anywhere on the sheet behind the drawer closes it, which is the gesture people try
          first — before they look for the control that opened it. */}
      {open ? (
        <button
          type="button"
          className={styles.navBackdrop}
          aria-label={t('chrome.nav.close')}
          onClick={onClose}
        />
      ) : null}
      <nav
        className={[styles.rail, open ? styles.railOpen : null].filter(Boolean).join(' ')}
        aria-label={t('chrome.nav.label')}
      >
      <span className={styles.mark} aria-hidden>
        {workspaceInitial}
      </span>

      {ROUTED.map(({ key, to, labelKey, Icon }) => {
        // A segment match rather than a bare prefix: `/tasks` must not light up for a path that
        // merely starts with the same letters, and `/projects/$id/tasks` belongs to Projects —
        // the project's own tab row says which tab, the rail only says which destination.
        const active = path === to || path.startsWith(`${to}/`);
        return (
          <Link
            key={key}
            to={to}
            className={[styles.railItem, active ? styles.railItemActive : null]
              .filter(Boolean)
              .join(' ')}
            {...(active ? { 'aria-current': 'page' as const } : {})}
            title={t(labelKey)}
          >
            <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
            <span className="sr-only">{t(labelKey)}</span>
          </Link>
        );
      })}

      {/* UX cleanup 2026-08-25 — the four dead rail items. See `PLACEHOLDERS` above.
      {PLACEHOLDERS.map(({ key, labelKey, Icon }) => (
        <button
          key={key}
          type="button"
          disabled
          className={[styles.railItem, styles.railItemDisabled].filter(Boolean).join(' ')}
          title={`${t(labelKey)} — ${unavailable}`}
        >
          <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
          <span className="sr-only">{`${t(labelKey)} — ${unavailable}`}</span>
        </button>
      ))}
      */}

      <div className={styles.railSpacer} />

      {canAdministerStudio ? (
        <Link
          to={SETTINGS.to}
          className={[styles.railItem, path.startsWith(SETTINGS.to) ? styles.railItemActive : null]
            .filter(Boolean)
            .join(' ')}
          {...(path.startsWith(SETTINGS.to) ? { 'aria-current': 'page' as const } : {})}
          title={t(SETTINGS.labelKey)}
        >
          <SETTINGS.Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
          <span className="sr-only">{t(SETTINGS.labelKey)}</span>
        </Link>
      ) : null}

      <AvatarMenu
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspaceId}
        userLabel={userLabel}
        userInitials={userInitials}
        onSignOut={onSignOut}
        signingOut={signingOut}
      />
      </nav>
    </>
  );
}
